import React from "react";
import styled, { createGlobalStyle, css } from "styled-components";
import { Avatar, Tooltip } from "@material-ui/core";

import { getAvatarColorForIdentifier } from "js/core/utilities/avatarGenerator";
import { useScreenSize } from "js/react/hooks/useScreenSize";

const COMPACT_VIEWPORT_MAX_WIDTH = 1200;

export interface CollaboratorProfile {
    photoURL?: string | null;
    displayName?: string;
    email?: string;
    /** True if the user has read-only (viewer) access to the resource */
    isReadOnly?: boolean;
}

const MAX_AVATARS = 3;
const TOOLTIP_CLASS = "collaborator-avatar-tooltip";

const StyledAvatar = styled(Avatar)<{ $backgroundColor?: string }>`
    &&& {
        width: 20px;
        height: 20px;
        margin-left: -5px;
        outline: 1px solid white;
        font-size: 11px;
        font-weight: 600;
        ${({ $backgroundColor }) => $backgroundColor && css`background-color: ${$backgroundColor};`}

        .MuiAvatar-img {
            object-fit: fill;
        }
    }
`;

const Stack = styled.div`
    display: flex;
    align-items: center;
    & > * { position: relative; }
    & > *:nth-child(1) { z-index: 3; }
    & > *:nth-child(2) { z-index: 2; }
    & > *:nth-child(3) { z-index: 1; }
`;

const TooltipStyles = createGlobalStyle`
    .${TOOLTIP_CLASS}.MuiTooltip-tooltip {
        font-size: 12px;
        font-weight: 400;
        padding: 8px;
        background: white;
        color: black;
        border-radius: 4px;
        max-width: 200px;
        border: 1px solid #e0e0e0;
        box-shadow: 0 2px 6px rgba(0,0,0,0.2);
    }
`;

export function getCollaboratorLabel(name: string, isCreator: boolean, isReadOnly?: boolean): string {
    if (isCreator) return `${name} (Creator)`;
    if (isReadOnly) return `${name} (View Only)`;
    return name;
}

const TooltipContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const AdditionalAvatars = ({
    profiles,
    markCreator = false,
}: {
    profiles: CollaboratorProfile[];
    markCreator?: boolean;
}) => {
    if (!profiles?.length) return null;

    const tooltip = (
        <TooltipContent>
            {profiles.map((p, i) => (
                <div key={p.email ?? p.displayName ?? i}>
                    {getCollaboratorLabel(p.displayName ?? p.email ?? "", markCreator && i === 0, p.isReadOnly)}
                </div>
            ))}
        </TooltipContent>
    );

    return (
        <Tooltip title={tooltip} placement="bottom" classes={{ tooltip: TOOLTIP_CLASS }}>
            <StyledAvatar $backgroundColor="#666">+{profiles.length}</StyledAvatar>
        </Tooltip>
    );
};

export default function CollaboratorAvatarStack({ profiles, isGrid = false }: { profiles: CollaboratorProfile[], isGrid?: boolean }) {
    const { width } = useScreenSize();
    const isCompact = width < COMPACT_VIEWPORT_MAX_WIDTH;

    if (!profiles?.length || profiles.length === 1) return null;

    // We always show the additional avatars in grid view
    if (isCompact && !isGrid) {
        return (
            <>
                <TooltipStyles />
                <AdditionalAvatars profiles={profiles} markCreator />
            </>
        );
    }

    const maxAvatars = profiles.length < 4 ? MAX_AVATARS : MAX_AVATARS - 1;

    const hasExtra = profiles.length > maxAvatars;
    const visible = hasExtra ? profiles.slice(0, maxAvatars) : profiles;
    const extraAvatars = hasExtra ? profiles.slice(maxAvatars) : [];

    return (
        <>
            <TooltipStyles />
            <Stack>
                {visible.map(({ displayName, email, photoURL, isReadOnly }, idx) => {
                    const name = displayName ?? email;
                    const hasPhoto = !!photoURL?.trim();

                    const avatarComponent = hasPhoto ? (<StyledAvatar
                        alt={name.toUpperCase()}
                        src={photoURL}
                    />) : (<StyledAvatar
                        alt={name.toUpperCase()}
                        $backgroundColor={getAvatarColorForIdentifier(name)}
                    >{name.charAt(0).toUpperCase()}</StyledAvatar>);

                    return (
                        <Tooltip
                            key={email ?? displayName ?? idx}
                            title={getCollaboratorLabel(name, idx === 0, isReadOnly)}
                            placement="bottom"
                            classes={{ tooltip: TOOLTIP_CLASS }}
                        >
                            {avatarComponent}
                        </Tooltip>
                    );
                })}
                <AdditionalAvatars profiles={extraAvatars} />
            </Stack>
        </>
    );
}
